/**
 * Card Media Swiper
 * - Mobile: Swiper.js between first image and second media (image or video)
 * - Desktop: Hover to reveal second media; autoplay video on hover
 *
 * SETUP:
 * 1. Include Swiper CSS + JS in your theme.liquid or relevant layout:
 *    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css">
 *    <script src="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js" defer></script>
 *
 * 2. Include this file after Swiper:
 *    {{ 'card-media-swiper.js' | asset_url | script_tag }}
 */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 990;
  let cardSwipers = [];

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  /* =====================
     MOBILE: Init Swiper
     ===================== */
  function initMobileSwipers() {
    const swiperEls = document.querySelectorAll('.card-media-swiper[data-has-second-media="true"]');

    swiperEls.forEach(function (el) {
      // Skip if already initialized
      if (el.swiper) return;

      var swiper = new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        loop: false,
        /*
         * threshold: px of movement before Swiper takes over.
         * Too low = conflicts with taps. Too high = feels unresponsive.
         * 8px is a good balance for product card swiping.
         */
        threshold: 8,
        /*
         * touchAngle: max angle (degrees) from horizontal that still counts
         * as a swipe. 35° means diagonal-ish gestures still trigger the swipe,
         * but clearly vertical scrolls are ignored.
         */
        touchAngle: 35,
        touchEventsTarget: 'container',
        /* Let the browser handle scrolling; Swiper handles horizontal */
        touchStartPreventDefault: false,
        passiveListeners: true,
        /* Resistance at edges so it feels bounded */
        resistance: true,
        resistanceRatio: 0.85,

        pagination: {
          el: el.querySelector('.card-swiper-pagination'),
          clickable: true,
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
        },
        on: {
          slideChangeTransitionEnd: function () {
            handleMobileSlideChange(this);
          },
          /* Tap (not swipe) → navigate to product page */
          tap: function (swiper, event) {
            if (event && event.target && event.target.closest('.card-swiper-pagination')) return;
            var wrapper = el.closest('.card-wrapper');
            if (!wrapper) return;
            var link = wrapper.querySelector('.card__heading a[href]');
            if (link) {
              window.location.href = link.href;
            }
          },
        },
      });

      cardSwipers.push(swiper);
    });
  }

  function destroyMobileSwipers() {
    cardSwipers.forEach(function (swiper) {
      if (swiper && !swiper.destroyed) {
        swiper.destroy(true, true);
      }
    });
    cardSwipers = [];
  }

  function handleMobileSlideChange(swiper) {
    var activeSlide = swiper.slides[swiper.activeIndex];
    var video = activeSlide ? activeSlide.querySelector('video') : null;

    // Pause all videos in this swiper first
    swiper.slides.forEach(function (slide) {
      var v = slide.querySelector('video');
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });

    // Play video in active slide
    if (video) {
      video.play().catch(function () {
        // Autoplay may be blocked — poster image shows as fallback
      });
    }
  }

  /* =====================
     DESKTOP: Hover video
     ===================== */
  function initDesktopHover() {
    var cards = document.querySelectorAll('.card-media-swiper[data-has-second-media="true"]');

    cards.forEach(function (swiperEl) {
      var wrapper = swiperEl.closest('.card-wrapper');
      if (!wrapper || wrapper._cardHoverBound) return;

      var video = swiperEl.querySelector('.swiper-slide:nth-child(2) video');
      if (!video) return;

      wrapper.addEventListener('mouseenter', function () {
        if (isMobile()) return;
        video.currentTime = 0;
        video.play().catch(function () {});
      });

      wrapper.addEventListener('mouseleave', function () {
        if (isMobile()) return;
        video.pause();
        video.currentTime = 0;
      });

      wrapper._cardHoverBound = true;
    });
  }

  /* =====================
     Responsive handler
     ===================== */
  function handleResize() {
    if (isMobile()) {
      initMobileSwipers();
    } else {
      destroyMobileSwipers();
    }
  }

  /* =====================
     Init
     ===================== */
  function init() {
    handleResize();
    initDesktopHover();
  }

  function waitForSwiper(callback) {
    if (typeof Swiper !== 'undefined') {
      callback();
    } else {
      var attempts = 0;
      var interval = setInterval(function () {
        attempts++;
        if (typeof Swiper !== 'undefined') {
          clearInterval(interval);
          callback();
        } else if (attempts > 50) {
          clearInterval(interval);
          console.warn('Card Media Swiper: Swiper.js not found. Mobile swiping disabled.');
          initDesktopHover();
        }
      }, 100);
    }
  }

  // Debounced resize
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(handleResize, 150);
  });

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForSwiper(init); });
  } else {
    waitForSwiper(init);
  }

  // Re-init when Shopify sections are loaded/reordered (theme editor + AJAX)
  document.addEventListener('shopify:section:load', function () { waitForSwiper(init); });
})();